/**
 * Global tooltip - position:fixed no body, nunca clipado por overflow:hidden.
 * Uso: data-tooltip="Texto" em qualquer elemento.
 * Init: mountTooltip() em App.js (com cleanup no unmount).
 */
export function mountTooltip() {
  const GAP    = 10;
  const MARGIN = 10;

  const wrap = document.createElement("div");
  Object.assign(wrap.style, {
    position:      "fixed",
    zIndex:        "999999",
    pointerEvents: "none",
    opacity:       "0",
    left:          "0",
    top:           "0",
    transform:     "translateX(-50%) translateY(-100%) scale(0.92)",
  });

  const tip = document.createElement("div");
  Object.assign(tip.style, {
    background:   "rgba(8,14,28,0.95)",
    color:        "#dde3ef",
    fontSize:     "12px",
    fontWeight:   "500",
    lineHeight:   "1.55",
    padding:      "7px 13px",
    borderRadius: "9px",
    border:       "1px solid rgba(255,255,255,0.10)",
    maxWidth:     "240px",
    boxShadow:    "0 8px 28px rgba(0,0,0,0.55)",
    textAlign:    "center",
    whiteSpace:   "normal",
    position:     "relative",
  });

  const arrow = document.createElement("div");
  Object.assign(arrow.style, {
    position:   "absolute",
    width:      "0",
    height:     "0",
    borderLeft: "6px solid transparent",
    borderRight:"6px solid transparent",
    left:       "50%",
    transform:  "translateX(-50%)",
  });

  tip.appendChild(arrow);
  wrap.appendChild(tip);
  document.body.appendChild(wrap);

  let hideTimer  = null;
  let rafId      = null;
  let activeEl   = null;
  let flipBelow  = false;

  function setText(text) {
    // preserva o nó de seta, atualiza ou cria o nó de texto antes dela
    if (tip.firstChild && tip.firstChild.nodeType === 3) {
      tip.firstChild.textContent = text;
    } else {
      tip.insertBefore(document.createTextNode(text), tip.firstChild);
    }
  }

  function setArrow(below) {
    if (below) {
      Object.assign(arrow.style, {
        top:          "-6px", bottom: "",
        borderBottom: "6px solid rgba(8,14,28,0.95)",
        borderTop:    "",
      });
    } else {
      Object.assign(arrow.style, {
        bottom:    "-6px", top: "",
        borderTop: "6px solid rgba(8,14,28,0.95)",
        borderBottom: "",
      });
    }
  }

  function place(el) {
    const r  = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const tw = wrap.offsetWidth;
    const th = wrap.offsetHeight;

    flipBelow = r.top - GAP - th < MARGIN;

    // X: centrado no elemento, clamped ao viewport
    let cx = r.left + r.width / 2;
    cx = Math.max(MARGIN + tw / 2, Math.min(vw - MARGIN - tw / 2, cx));

    // Y: âncora no topo ou no fundo do elemento
    const cy = flipBelow ? r.bottom + GAP : r.top - GAP;

    wrap.style.left = cx + "px";
    wrap.style.top  = cy + "px";

    // ajusta X da seta para apontar ao elemento original
    const arrowOff = r.left + r.width / 2 - cx;
    arrow.style.left = `calc(50% + ${arrowOff}px)`;
    setArrow(flipBelow);
  }


  function show(e) {
    const el = e.target && e.target.closest("[data-tooltip]");
    if (!el) return;
    if (el === activeEl) { clearTimeout(hideTimer); return; }

    activeEl = el;
    clearTimeout(hideTimer);
    cancelAnimationFrame(rafId);

    setText(el.getAttribute("data-tooltip"));

    // Posiciona com opacity:0 para medir sem flash
    wrap.style.transition = "none";
    wrap.style.opacity    = "0";
    wrap.style.transform  = "translateX(-50%) translateY(-96%) scale(0.92)";

    place(el);

    // Duplo rAF: garante que o frame sem transição foi pintado
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        const yIn  = flipBelow ? "translateY(0%)"    : "translateY(-100%)";
        const yOut = flipBelow ? "translateY(4%)"    : "translateY(-96%)";
        wrap.style.transition = "none";
        wrap.style.transform  = `translateX(-50%) ${yOut} scale(0.88)`;
        wrap.style.opacity    = "0";
        rafId = requestAnimationFrame(() => {
          wrap.style.transition = "opacity 0.14s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)";
          wrap.style.opacity    = "1";
          wrap.style.transform  = `translateX(-50%) ${yIn} scale(1)`;
        });
      });
    });
  }

  function hide(e) {
    if (e.relatedTarget && e.relatedTarget.closest
        && e.relatedTarget.closest("[data-tooltip]")) return;

    activeEl = null;
    clearTimeout(hideTimer);
    cancelAnimationFrame(rafId);

    hideTimer = setTimeout(() => {
      const yOut = flipBelow ? "translateY(4%)" : "translateY(-96%)";
      wrap.style.transition = "opacity 0.10s ease, transform 0.10s ease";
      wrap.style.opacity    = "0";
      wrap.style.transform  = `translateX(-50%) ${yOut} scale(0.94)`;
    }, 60);
  }

  document.addEventListener("mouseover", show);
  document.addEventListener("mouseout",  hide);

  return () => {
    document.removeEventListener("mouseover", show);
    document.removeEventListener("mouseout",  hide);
    clearTimeout(hideTimer);
    cancelAnimationFrame(rafId);
    if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
  };
}
